import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Stack,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import { Add, Check, Close, Delete, Edit } from "@mui/icons-material";
import LinearProgress from "@mui/material/LinearProgress";
import unsplash from "../api/unsplash";
import GlobalSnackbar from "./Snackbar";
import {
  DataGridPro,
  GridToolbarContainer,
  GridToolbarQuickFilter,
  itIT,
} from "../utils/PersistedDataGridPro";
import { CustomFooterPersonalized } from "../utils/CustomFooterPersonalized";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Zoom timeout={2000} in={true} ref={ref} {...props} />;
});

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem("jwt")}`,
});

const emptyCcnl = {
  Descrizione: "",
};

const emptyInquadramento = {
  ID: null,
  IDTipologia: 0,
  IDLivello: 0,
  DescrizioneLivello: "",
  Descrizione: "",
};

const CcnlParita = () => {
  const [contratti, setContratti] = useState([]);
  const [inquadramenti, setInquadramenti] = useState([]);
  const [selectedContrattoId, setSelectedContrattoId] = useState(null);
  const [loadingContratti, setLoadingContratti] = useState(true);
  const [loadingInquadramenti, setLoadingInquadramenti] = useState(true);
  const [contrattoDialog, setContrattoDialog] = useState({
    open: false,
    mode: "new",
    value: emptyCcnl,
  });
  const [inquadramentoDialog, setInquadramentoDialog] = useState({
    open: false,
    mode: "new",
    value: emptyInquadramento,
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    type: null,
    row: null,
  });
  const [openSnack, setOpenSnack] = useState(false);
  const [snack, setSnack] = useState({
    severity: "success",
    messaggio: "Salvataggio effettuato correttamente",
  });

  const selectedContratto = useMemo(
    () => contratti.find((contratto) => contratto.ID === selectedContrattoId),
    [contratti, selectedContrattoId],
  );

  const inquadramentiFiltrati = useMemo(
    () =>
      inquadramenti.filter(
        (inquadramento) => inquadramento.IDTipologia === selectedContrattoId,
      ),
    [inquadramenti, selectedContrattoId],
  );

  const showSnack = (severity, messaggio) => {
    setSnack({ severity, messaggio });
    setOpenSnack(true);
  };

  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack(false);
  };

  const loadContratti = async () => {
    setLoadingContratti(true);
    const response = await unsplash.get("tipologieCcnlParita", {
      headers: authHeaders(),
    });
    const data = response.data.data || [];
    setContratti(data);
    setSelectedContrattoId((currentId) => currentId || data[0]?.ID || null);
    setLoadingContratti(false);
  };

  const loadInquadramenti = async () => {
    setLoadingInquadramenti(true);
    const response = await unsplash.get("ccnlParita", {
      headers: authHeaders(),
    });
    setInquadramenti(response.data.data || []);
    setLoadingInquadramenti(false);
  };

  const reloadAll = async () => {
    await Promise.all([loadContratti(), loadInquadramenti()]);
  };

  useEffect(() => {
    reloadAll().catch((error) => {
      console.error(error.message);
      showSnack("error", "Errore durante il caricamento dei CCNL");
      setLoadingContratti(false);
      setLoadingInquadramenti(false);
    });
  }, []);

  const handleSaveContratto = async () => {
    try {
      if (contrattoDialog.mode === "new") {
        const response = await unsplash.post(
          "tipologieCcnlParita",
          { tipologie: contrattoDialog.value },
          { headers: authHeaders() },
        );
        const insertId = response.data?.message?.insertId;
        await loadContratti();
        if (insertId) {
          setSelectedContrattoId(insertId);
        }
        showSnack("success", "CCNL applicato creato con successo");
      } else {
        await unsplash.put(
          "tipologieCcnlParita",
          { tipologie: contrattoDialog.value },
          { headers: authHeaders() },
        );
        await loadContratti();
        showSnack("success", "CCNL applicato aggiornato con successo");
      }
      setContrattoDialog({ open: false, mode: "new", value: emptyCcnl });
    } catch {
      showSnack("error", "Errore durante il salvataggio del CCNL applicato");
    }
  };

  const createLivello = async (descrizione) => {
    const response = await unsplash.post(
      "livelliParita",
      { livello: { Descrizione: descrizione } },
      { headers: authHeaders() },
    );
    return response.data?.message?.insertId;
  };

  const handleSaveInquadramento = async () => {
    if (!selectedContrattoId) {
      showSnack("error", "Seleziona un CCNL prima di inserire un livello");
      return;
    }
    try {
      const livello = inquadramentoDialog.value.DescrizioneLivello?.trim();
      if (!livello) {
        showSnack("error", "Inserisci il livello");
        return;
      }

      let idLivello = inquadramentoDialog.value.IDLivello;
      if (
        inquadramentoDialog.mode === "new" ||
        livello !== inquadramentoDialog.value.DescrizioneLivelloOriginale
      ) {
        idLivello = await createLivello(livello);
      }
      if (!idLivello) {
        showSnack("error", "Errore durante la creazione del livello");
        return;
      }

      const payload = {
        ...inquadramentoDialog.value,
        IDTipologia: selectedContrattoId,
        IDLivello: idLivello,
      };

      if (inquadramentoDialog.mode === "new") {
        await unsplash.post(
          "ccnlParita",
          { ccnl: payload },
          { headers: authHeaders() },
        );
        showSnack("success", "Livello e inquadramento creati con successo");
      } else {
        await unsplash.put(
          "ccnlParita",
          { ccnl: payload },
          { headers: authHeaders() },
        );
        showSnack("success", "Livello e inquadramento aggiornati con successo");
      }

      await loadInquadramenti();
      setInquadramentoDialog({
        open: false,
        mode: "new",
        value: emptyInquadramento,
      });
    } catch {
      showSnack("error", "Errore durante il salvataggio del livello");
    }
  };

  const handleDelete = async () => {
    try {
      if (deleteDialog.type === "contratto") {
        await unsplash.delete("tipologieCcnlParita", {
          headers: authHeaders(),
          data: { id: deleteDialog.row.ID },
        });
        await reloadAll();
        setSelectedContrattoId(null);
        showSnack("success", "CCNL applicato eliminato con successo");
      }
      if (deleteDialog.type === "inquadramento") {
        await unsplash.delete("ccnlParita", {
          headers: authHeaders(),
          data: { id: deleteDialog.row.ID },
        });
        await loadInquadramenti();
        showSnack("success", "Livello eliminato dal CCNL con successo");
      }
      setDeleteDialog({ open: false, type: null, row: null });
    } catch {
      showSnack("error", "Errore durante l'eliminazione");
    }
  };

  const columnsContratti = [
    {
      filterable: false,
      field: "Actions",
      headerName: "Azioni",
      width: 105,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton
            aria-label="modifica ccnl"
            onClick={(event) => {
              event.stopPropagation();
              setSelectedContrattoId(params.row.ID);
              setContrattoDialog({
                open: true,
                mode: "edit",
                value: params.row,
              });
            }}
          >
            <Edit
              sx={{ height: "1.7rem", width: "1.7rem", color: "#2265B1" }}
            />
          </IconButton>
          <IconButton
            aria-label="elimina ccnl"
            onClick={(event) => {
              event.stopPropagation();
              setDeleteDialog({
                open: true,
                type: "contratto",
                row: params.row,
              });
            }}
          >
            <Delete
              sx={{ height: "1.7rem", width: "1.7rem", color: "#FF2768" }}
            />
          </IconButton>
        </>
      ),
    },
    {
      field: "Descrizione",
      headerName: "CCNL Applicato",
      flex: 1,
      minWidth: 180,
    },
  ];

  const columnsInquadramenti = [
    {
      filterable: false,
      field: "Actions",
      headerName: "Azioni",
      width: 105,
      headerAlign: "center",
      align: "center",
      renderCell: (params) => (
        <>
          <IconButton
            aria-label="modifica livello"
            onClick={() =>
              setInquadramentoDialog({
                open: true,
                mode: "edit",
                value: {
                  ...params.row,
                  DescrizioneLivelloOriginale: params.row.DescrizioneLivello,
                },
              })
            }
          >
            <Edit
              sx={{ height: "1.7rem", width: "1.7rem", color: "#2265B1" }}
            />
          </IconButton>
          <IconButton
            aria-label="elimina livello"
            onClick={() =>
              setDeleteDialog({
                open: true,
                type: "inquadramento",
                row: params.row,
              })
            }
          >
            <Delete
              sx={{ height: "1.7rem", width: "1.7rem", color: "#FF2768" }}
            />
          </IconButton>
        </>
      ),
    },
    {
      field: "DescrizioneLivello",
      headerName: "Livello",
      width: 160,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "Descrizione",
      headerName: "Inquadramento",
      flex: 1,
      minWidth: 320,
    },
  ];

  function CustomToolbar() {
    return (
      <GridToolbarContainer>
        <Grid
          container
          direction="row"
          justifyContent="flex-start"
          alignItems="center"
        >
          <GridToolbarQuickFilter placeholder="Cerca..." />
        </Grid>
      </GridToolbarContainer>
    );
  }

  return (
    <>
      <GlobalSnackbar
        severity={snack.severity}
        messaggio={snack.messaggio}
        openSnack={openSnack}
        handleCloseSnack={handleCloseSnack}
      />
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" mb={2}>
                <Typography variant="h6">CCNL Applicati</Typography>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  className="ButtonMiller"
                  onClick={() =>
                    setContrattoDialog({
                      open: true,
                      mode: "new",
                      value: emptyCcnl,
                    })
                  }
                >
                  Aggiungi
                </Button>
              </Stack>
              <Box sx={{ height: { xs: "35vh", md: "68vh" }, width: 1 }}>
                <DataGridPro
                  components={{
                    Toolbar: CustomToolbar,
                    Footer: CustomFooterPersonalized,
                  }}
                  slots={{ loadingOverlay: LinearProgress }}
                  loading={loadingContratti}
                  localeText={
                    itIT.components.MuiDataGrid.defaultProps.localeText
                  }
                  rowHeight={52}
                  rows={contratti}
                  columns={columnsContratti}
                  getRowId={(row) => row.ID}
                  pageSize={20}
                  rowsPerPageOptions={[20]}
                  disableSelectionOnClick
                  onRowClick={(params) => setSelectedContrattoId(params.row.ID)}
                  selectionModel={
                    selectedContrattoId ? [selectedContrattoId] : []
                  }
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog
        open={contrattoDialog.open}
        onClose={() =>
          setContrattoDialog({ open: false, mode: "new", value: emptyCcnl })
        }
        fullWidth
        maxWidth={contrattoDialog.mode === "edit" ? "lg" : "sm"}
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ mx: "auto" }}>
          {contrattoDialog.mode === "new"
            ? "Nuovo CCNL Applicato"
            : "Modifica CCNL Applicato"}
        </DialogTitle>
        <DialogContent>
          <TextField
            label="CCNL Applicato"
            name="Descrizione"
            value={contrattoDialog.value.Descrizione || ""}
            onChange={(event) =>
              setContrattoDialog((current) => ({
                ...current,
                value: {
                  ...current.value,
                  Descrizione: event.target.value,
                },
              }))
            }
            fullWidth
            sx={{ mt: 2 }}
          />
          {contrattoDialog.mode === "edit" && (
            <Box sx={{ mt: 4 }}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                spacing={2}
                mb={2}
              >
                <Box>
                  <Typography variant="h6">Livelli e Inquadramenti</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {contrattoDialog.value.Descrizione || "CCNL selezionato"}
                  </Typography>
                </Box>
                <Button
                  startIcon={<Add />}
                  variant="contained"
                  className="ButtonMiller"
                  onClick={() =>
                    setInquadramentoDialog({
                      open: true,
                      mode: "new",
                      value: {
                        ...emptyInquadramento,
                        IDTipologia: contrattoDialog.value.ID,
                      },
                    })
                  }
                >
                  Aggiungi livello
                </Button>
              </Stack>
              <Box sx={{ height: { xs: "45vh", md: "48vh" }, width: 1 }}>
                <DataGridPro
                  initialState={{ pinnedColumns: { left: ["Actions"] } }}
                  components={{
                    Toolbar: CustomToolbar,
                    Footer: CustomFooterPersonalized,
                  }}
                  slots={{ loadingOverlay: LinearProgress }}
                  loading={loadingInquadramenti}
                  localeText={
                    itIT.components.MuiDataGrid.defaultProps.localeText
                  }
                  rowHeight={72}
                  rows={inquadramentiFiltrati}
                  columns={columnsInquadramenti}
                  getRowId={(row) => row.ID}
                  pageSize={20}
                  rowsPerPageOptions={[20]}
                  disableSelectionOnClick
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", mx: 2 }}>
          <Button
            onClick={() =>
              setContrattoDialog({ open: false, mode: "new", value: emptyCcnl })
            }
            variant="contained"
            className="ButtonMiller"
            startIcon={<Close />}
          >
            Chiudi
          </Button>
          <Button
            onClick={handleSaveContratto}
            variant="contained"
            className="ButtonMiller"
            startIcon={<Check />}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={inquadramentoDialog.open}
        onClose={() =>
          setInquadramentoDialog({
            open: false,
            mode: "new",
            value: emptyInquadramento,
          })
        }
        fullWidth
        maxWidth="lg"
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ mx: "auto" }}>
          {inquadramentoDialog.mode === "new"
            ? "Nuovo Livello"
            : "Modifica Livello"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            <Grid item xs={12} md={4}>
              <TextField
                label="CCNL Applicato"
                value={selectedContratto?.Descrizione || ""}
                fullWidth
                disabled
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField
                label="Livello"
                name="DescrizioneLivello"
                value={inquadramentoDialog.value.DescrizioneLivello || ""}
                onChange={(event) =>
                  setInquadramentoDialog((current) => ({
                    ...current,
                    value: {
                      ...current.value,
                      DescrizioneLivello: event.target.value,
                    },
                  }))
                }
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                multiline
                rows={7}
                label="Inquadramento"
                name="Descrizione"
                value={inquadramentoDialog.value.Descrizione || ""}
                onChange={(event) =>
                  setInquadramentoDialog((current) => ({
                    ...current,
                    value: {
                      ...current.value,
                      Descrizione: event.target.value,
                    },
                  }))
                }
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", mx: 2 }}>
          <Button
            onClick={() =>
              setInquadramentoDialog({
                open: false,
                mode: "new",
                value: emptyInquadramento,
              })
            }
            variant="contained"
            className="ButtonMiller"
            startIcon={<Close />}
          >
            Chiudi
          </Button>
          <Button
            onClick={handleSaveInquadramento}
            variant="contained"
            className="ButtonMiller"
            startIcon={<Check />}
          >
            Salva
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, type: null, row: null })}
      >
        <DialogTitle sx={{ mx: "auto" }}>Conferma eliminazione</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Sei sicuro di voler eliminare{" "}
            <b>
              {deleteDialog.type === "contratto"
                ? deleteDialog.row?.Descrizione
                : deleteDialog.row?.DescrizioneLivello}
            </b>
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3 }}>
          <Button
            onClick={() =>
              setDeleteDialog({ open: false, type: null, row: null })
            }
          >
            Annulla
          </Button>
          <Button onClick={handleDelete}>Conferma</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CcnlParita;
